import { Injectable } from '@angular/core';

import moment from 'moment-timezone';

import { DataPointModel } from '../../Charts/models/data-point.model';
import { ChartModel } from '../../Charts/models/chart.model';
import { Am5Chart } from '../../Charts/models/am5-chart.model';
import { ReportBlockTypeEnum, reportBlockTypeEnumFromApi } from '../enums/report-block-type.enum';
import { reportsBlockMappingConfigurationMap, ReportsDataMappingKeysEnum } from '../models/reports-block-mapping-configuration.model';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { safeDivide } from '../../../Core/utils';

export type DateGroupingScale = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';

// ordered from finest to coarsest, index doubles as a coarseness rank
const scaleCoarsenessOrder: DateGroupingScale[] = ['hour', 'day', 'week', 'month', 'quarter', 'year'];

// if the combined range spans more than 36 units of a given scale, that scale would produce an unreadable
// number of chart points, so escalate to at least the paired coarser scale
const escalationRules: { spanUnit: 'days' | 'weeks' | 'months'; maxSpan: number; minScale: DateGroupingScale }[] = [
    { spanUnit: 'days', maxSpan: 36, minScale: 'week' },
    { spanUnit: 'weeks', maxSpan: 36, minScale: 'month' },
    { spanUnit: 'months', maxSpan: 36, minScale: 'quarter' }
];

@Injectable()
export class ReportsSeriesDataGrouperService {

    /**
     * When folders/sources are queried on an "all time" range, each one can come back from the backend at a
     * different granularity depending on how far back its own data spans (a folder with years of mentions
     * comes back monthly, one with a single day of mentions comes back hourly). Left alone, ReportsSeriesDataMergerService
     * would merge those mismatched scales point for point, which is meaningless (e.g. summing a month of daily
     * points into what looks like one "day"). This regroups every series onto one common scale first - the
     * coarsest scale actually needed to represent the widest series, escalated further if the combined date
     * range is long enough that even that scale would produce too many points.
     * Pass escalate = false to only align the scales, e.g. to get the ungrouped data points for export.
     */
    normalizeScale(data: DataPointModel[][], chart: ChartModel | Am5Chart, escalate: boolean = true): DataPointModel[][] {
        if (!this.appliesToChart(chart)) {
            return data;
        }

        const allPoints = data.flat().filter(point => !!point && !!point.timeScale);
        if (!allPoints.length) {
            return data;
        }

        const targetScale = this.determineTargetScale(allPoints, escalate);
        const distinctScales = new Set(allPoints.map(point => point.timeScale));
        if (distinctScales.size === 1 && distinctScales.has(targetScale)) {
            // already a single common scale and no need to escalate further
            return data;
        }

        return data.map(seriesData => this.groupSeriesToScale(seriesData, targetScale));
    }

    private appliesToChart(chart: ChartModel | Am5Chart): boolean {
        let chartType: ReportBlockTypeEnum;
        if (chart instanceof ChartModel) {
            chartType = chart.metadata.type;
        } else if (chart instanceof Am5Chart) {
            chartType = chart.config.type;
        }

        const chartConfig = reportsBlockMappingConfigurationMap.get(chartType)
            || reportsBlockMappingConfigurationMap.get(reportBlockTypeEnumFromApi.get(chartType as any as ReportBlockTypeApiEnum));

        return chartConfig?.mappingKey === ReportsDataMappingKeysEnum.DATE;
    }

    private determineTargetScale(points: DataPointModel[], escalate: boolean): DateGroupingScale {
        let coarsestRank = 0;
        points.forEach(point => {
            const rank = scaleCoarsenessOrder.indexOf(point.timeScale as DateGroupingScale);
            if (rank > coarsestRank) {
                coarsestRank = rank;
            }
        });

        const range = escalate ? this.getOverallDateRange(points) : null;
        if (range) {
            escalationRules.forEach(rule => {
                const span = range.end.diff(range.start, rule.spanUnit, true);
                if (span > rule.maxSpan) {
                    coarsestRank = Math.max(coarsestRank, scaleCoarsenessOrder.indexOf(rule.minScale));
                }
            });
        }

        return scaleCoarsenessOrder[coarsestRank];
    }

    private getOverallDateRange(points: DataPointModel[]): { start: moment.Moment; end: moment.Moment } {
        const starts = points.map(point => point.rawStartDate ?? point.mappingKeys?.[ReportsDataMappingKeysEnum.DATE])
            .filter(date => date !== undefined && date !== null);
        const ends = points.map(point => point.rawEndDate ?? point.mappingKeys?.[ReportsDataMappingKeysEnum.DATE])
            .filter(date => date !== undefined && date !== null);
        if (!starts.length || !ends.length) {
            return null;
        }

        return {
            start: moment(Math.min(...starts.map(Number))),
            end: moment(Math.max(...ends.map(Number)))
        };
    }

    private groupSeriesToScale(seriesData: DataPointModel[], targetScale: DateGroupingScale): DataPointModel[] {
        if (!seriesData?.length) {
            return seriesData;
        }

        const groups = new Map<string, DataPointModel[]>();
        let ungroupableIndex = 0;
        seriesData.forEach(point => {
            const date = point.mappingKeys?.[ReportsDataMappingKeysEnum.DATE];
            // points without a date can't be scale-aligned; keep them as their own untouched group
            const key = date === undefined || date === null
                ? `__ungroupable_${ungroupableIndex++}`
                : this.buildGroupKey(point, date, targetScale);

            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key).push(point);
        });

        const mergedPoints = Array.from(groups.values()).map(group => this.mergeGroup(group, targetScale));
        mergedPoints.sort((a, b) => (a.mappingKeys?.[ReportsDataMappingKeysEnum.DATE] ?? 0) - (b.mappingKeys?.[ReportsDataMappingKeysEnum.DATE] ?? 0));
        mergedPoints.forEach((point, index) => {
            if (point.mappingKeys) {
                point.mappingKeys[ReportsDataMappingKeysEnum.DATE_INDEX] = index;
            }
        });

        return mergedPoints;
    }

    private buildGroupKey(point: DataPointModel, date: number, targetScale: DateGroupingScale): string {
        const bucketStart = moment(date).startOf(targetScale).valueOf();
        const otherKeys = { ...point.mappingKeys };
        delete otherKeys[ReportsDataMappingKeysEnum.DATE];
        delete otherKeys[ReportsDataMappingKeysEnum.DATE_INDEX];

        return `${bucketStart}|${JSON.stringify(otherKeys)}`;
    }

    private mergeGroup(group: DataPointModel[], targetScale: DateGroupingScale): DataPointModel {
        if (group.length === 1 && group[0].timeScale === targetScale) {
            return group[0];
        }

        const mergedPoint: DataPointModel = JSON.parse(JSON.stringify(group[0]));
        const date = mergedPoint.mappingKeys?.[ReportsDataMappingKeysEnum.DATE];
        if (!date) {
            return mergedPoint;
        }

        const bucketStart = moment(date).startOf(targetScale);
        const bucketEnd = bucketStart.clone().endOf(targetScale);
        const rawStartDate = Math.min(...group.map(point => Number(point.rawStartDate ?? bucketStart.valueOf())));
        const rawEndDate = Math.max(...group.map(point => Number(point.rawEndDate ?? bucketEnd.valueOf())));

        mergedPoint.mappingKeys[ReportsDataMappingKeysEnum.DATE] = bucketStart.valueOf();
        mergedPoint.value = group.reduce((sum, point) => sum + (+point.value || 0), 0);
        if (group.some(point => point.secondaryValue !== undefined && point.secondaryValue !== null)) {
            mergedPoint.secondaryValue = group.reduce((sum, point) => sum + (+point.secondaryValue || 0), 0);
        }
        mergedPoint.mentionCount = group.reduce((sum, point) => sum + (+point.mentionCount || 0), 0);
        if (group.some(point => point.percent !== undefined && point.percent !== null)) {
            mergedPoint.percent = parseFloat((safeDivide(mergedPoint.value, mergedPoint.mentionCount) * 100).toFixed(1));
        }
        mergedPoint.rawStartDate = rawStartDate;
        mergedPoint.rawEndDate = rawEndDate;
        mergedPoint.startTimeExport = bucketStart.format('YYYY/MM/DD HH:mm');
        mergedPoint.endTimeExport = bucketEnd.format('YYYY/MM/DD HH:mm');
        mergedPoint.timeScale = targetScale;
        mergedPoint.viewUrl = this.rebuildViewUrl(mergedPoint.viewUrl as string, rawStartDate, rawEndDate);

        return mergedPoint;
    }

    private rebuildViewUrl(viewUrl: string, rawStartDate: number, rawEndDate: number): string {
        if (!viewUrl?.includes('filters=')) {
            return viewUrl;
        }

        const [ prefix, encodedFilters ] = viewUrl.split('filters=');
        const searchCriteriaModel = MentionSearchCriteriaModel.fromFilterQueryString(encodedFilters);
        if (!searchCriteriaModel?.search) {
            return viewUrl;
        }

        searchCriteriaModel.search.query.dateQuery.absoluteRange = {
            start: new Date(rawStartDate),
            end: new Date(rawEndDate)
        };

        return prefix + 'filters=' + MentionSearchCriteriaModel.toFilterQueryString(searchCriteriaModel.search, searchCriteriaModel.seriesUniqueUuid);
    }
}
